import { TAG_VARIANTS } from '@private/components';

import { IAllPageFilters, TObject } from 'utils/types';

export enum LOG_CHANGE_TYPE {
  ADDED = 'ADDED',
  CHANGED = 'CHANGED',
  REMOVED = 'REMOVED',
}

export enum LOG_OPERATION_TYPE {
  CREATED = 'CREATED',
  DELETED = 'DELETED',
  PASSWORD_RESET_COMPLETED = 'PASSWORD_RESET_COMPLETED',
  PASSWORD_RESET_REQUESTED = 'PASSWORD_RESET_REQUESTED',
  UPDATED = 'UPDATED',
}

export interface IAuditLog {
  authorId: string;
  authorLogin: string;
  changes: Record<string, IAuditLogChange>;
  creationDate: string;
  operationType: LOG_OPERATION_TYPE;
  parentId: string;
  parentType: AUDIT_LOG_TARGET_TYPES;
  targetId: string;
  targetType: AUDIT_LOG_TARGET_TYPES;
  uuid: string;
}

export interface IAuditLogChange {
  changeType: string;
  elements: IAuditLogChange[];
  from: string | TObject;
  to: string | TObject;
  value: string | TObject;
}

export interface IAuditLogLeafChange {
  change: IAuditLogChange;
  path: string[];
}

export const LOG_OPERATION_TAGS = {
  [LOG_OPERATION_TYPE.CREATED]: TAG_VARIANTS.SUCCESS,
  [LOG_OPERATION_TYPE.DELETED]: TAG_VARIANTS.ERROR,
  [LOG_OPERATION_TYPE.PASSWORD_RESET_COMPLETED]: TAG_VARIANTS.BLUE,
  [LOG_OPERATION_TYPE.PASSWORD_RESET_REQUESTED]: TAG_VARIANTS.PRIMARY,
  [LOG_OPERATION_TYPE.UPDATED]: TAG_VARIANTS.WARNING,
};

export const LOG_OPERATION_STATUS_INTL = {
  [LOG_OPERATION_TYPE.CREATED]: 'common.created',
  [LOG_OPERATION_TYPE.DELETED]: 'common.deleted',
  [LOG_OPERATION_TYPE.PASSWORD_RESET_COMPLETED]: 'common.passwordResetCompleted',
  [LOG_OPERATION_TYPE.PASSWORD_RESET_REQUESTED]: 'common.passwordResetRequested',
  [LOG_OPERATION_TYPE.UPDATED]: 'common.updated',
};

export enum AUDIT_LOG_TARGET_TYPES {
  API_USER = 'API_USER',
  BANK = 'BANK',
  BANK_PARAMETER = 'BANK_PARAMETER',
  BANK_TERMINAL = 'BANK_TERMINAL',
  BANK_TERMINAL_PARAMETER = 'BANK_TERMINAL_PARAMETER',
  BILLING_CONTRACT = 'BILLING_CONTRACT',
  BIN_GROUP = 'BIN_GROUP',
  BLACKLIST_EMAIL = 'BLACKLIST_EMAIL',
  BLACKLIST_MASKED_PAN = 'BLACKLIST_MASKED_PAN',
  CASCADING_SCHEMA = 'CASCADING_SCHEMA',
  CASHIER_PAYMENT_METHOD = 'CASHIER_PAYMENT_METHOD',
  CASHIER_SETTING = 'CASHIER_SETTING',
  COUNTRY_GROUP = 'COUNTRY_GROUP',
  CUSTOMER = 'CUSTOMER',
  CUSTOMER_ROUTING_PRIORITY = 'CUSTOMER_ROUTING_PRIORITY',
  DIRECT_PAYMENT_LINK = 'DIRECT_PAYMENT_LINK',
  GATEWAY_INVOICE = 'GATEWAY_INVOICE',
  LEGAL_ENTITY = 'LEGAL_ENTITY',
  LEGAL_ENTITY_DOCUMENT = 'LEGAL_ENTITY_DOCUMENT', // can be viewed only from the main entity
  MERCHANT = 'MERCHANT',
  MERCHANT_GROUP = 'MERCHANT_GROUP',
  MERCHANT_TERMINAL = 'MERCHANT_TERMINAL',
  PAYMENT_METHOD_LIMIT = 'PAYMENT_METHOD_LIMIT',
  RECON_RESOLVE = 'RECON_RESOLVE',
  ROLE = 'ROLE',
  ROUTING_RULESET = 'ROUTING_RULESET',
  ROUTING_SCHEMA = 'ROUTING_SCHEMA',
  SETTLEMENT_CONTRACT = 'SETTLEMENT_CONTRACT',
  TERMINAL_LINK = 'TERMINAL_LINK',
  TERMINAL_LINK_GROUP = 'TERMINAL_LINK_GROUP',
  TERMINAL_LINK_GROUP_LIMIT = 'TERMINAL_LINK_GROUP_LIMIT',
  TERMINAL_LINK_PSP_LIMIT = 'TERMINAL_LINK_PSP_LIMIT',
  TERMINAL_LINK_PSP_PARAMETER = 'TERMINAL_LINK_PSP_PARAMETER',
  TRANSACTION = 'TRANSACTION',
  TRANSACTION_OPERATION = 'TRANSACTION_OPERATION', // can be viewed only from the main entity
  USER = 'USER',
}

export const TARGET_TYPES_OPTIONS = Object.values(AUDIT_LOG_TARGET_TYPES);

export enum ACTION_STATUS_TYPE {
  FAILED = 'FAILED',
  SUCCESS = 'SUCCESS',
}

export const ACTION_STATUS_TYPE_VARIANTS = {
  [ACTION_STATUS_TYPE.FAILED]: TAG_VARIANTS.ERROR,
  [ACTION_STATUS_TYPE.SUCCESS]: TAG_VARIANTS.SUCCESS,
};

export interface IAuthAuditLog {
  actionStatus: ACTION_STATUS_TYPE;
  actionType: string;
  additionalInfo: string;
  created: string;
  id: string;
  ip: string;
  login: string;
  user: {
    id: string;
  };
  userAgent: string;
}

export type IAuthAuditLogData = TObject & {
  user: TObject;
};

export interface IGetAuditLogsListPayload {
  enabled?: boolean;
  filters: IAllPageFilters;
  targetId: number | string;
  targetType: AUDIT_LOG_TARGET_TYPES;
}
